import { TaxType } from "../types/costService";

type TaxLike = TaxType | string | null | undefined;

/** VAT rate as a fraction for a stored tax category. "0"/"NO_TAX" → 0. */
export const taxRate = (tax: TaxLike): number => (tax === "24" ? 0.24 : 0);

/** True when the row carries a VAT category (i.e. anything other than NO_TAX). */
export const hasTaxCategory = (tax: TaxLike): boolean =>
  !!tax && tax !== "NO_TAX";

/** True when at least one row in the list carries a VAT category. */
export const listHasTax = (rows: Array<{ tax?: TaxLike }>): boolean =>
  rows.some((r) => hasTaxCategory(r.tax));

/** Human label for a tax category shown per row (e.g. "24%", "0%", "—"). */
export const taxLabel = (tax: TaxLike): string =>
  hasTaxCategory(tax) ? `${tax}%` : "—";

/** Tax amount for a single row given its net (price × qty) line total. */
export const rowTaxAmount = (net: number, tax: TaxLike): number =>
  net * taxRate(tax);

/**
 * Per-row tax cell label combining the rate and the computed amount for a
 * given net line total, e.g. "24% (€12.00)". Falls back to "—" for NO_TAX.
 */
export const taxCellLabel = (net: number, tax: TaxLike): string =>
  hasTaxCategory(tax)
    ? `${tax}% (€${rowTaxAmount(net, tax).toFixed(2)})`
    : "—";

/** Sum of the tax amounts across a list of price/qty/tax rows. */
export const sumTax = (
  rows: Array<{ price?: number | null; qty?: number | null; tax?: TaxLike }>,
): number =>
  rows.reduce(
    (sum, r) => sum + rowTaxAmount((r.price || 0) * (r.qty || 0), r.tax),
    0,
  );
